/**
 * stats-panel-net-chart.js — the stats panel's net chart, the whole client half of
 * `templates/stats-shared.html`'s `netChart(stats)` fragment (leaderboard-row-daily-net-columns;
 * two surfaces since stats-panel-unification slice 2 — the expanded leaderboard row and `/games`).
 * Reads that fragment's `.sp-net-data` span for its `StatsPanelNetSeries.NetSeries` JSON, composes
 * the chart's rows, and mounts the form its `form` field names into the sibling `.sp-net-chart`
 * host: day columns (`charts/diverging-columns.js`) under a windowed scope, the progression line
 * (`charts/progression-line.js`) under All-Time. The form follows the WINDOW, never the surface —
 * the initiative's own rule (`initiatives/charts-everywhere/context.md`), which is why nothing here
 * branches on where it is rendering.
 *
 * Its own module beside `stats-panel-enhance.js` rather than a function inside it, for the reason
 * that file's own header would give: it sits at 397 logic lines against a 450 red, and this mount
 * plus its two row folds would put it over. The seam is a real one either way — the triad's icons,
 * charts and cross-highlight are one concern, and this second side channel is another. `enhance`
 * calls both, independently.
 *
 * The span is absent whenever the server sent no series at all (`netJson == null`, which the
 * fragment's own `th:if` reads): the entry-less `/stats/panel` shape (served, but with no caller
 * since result-stats-button-opens-games-overlay), which pays for no side channel
 * (`StatsPanelViewBuilder`'s `leaderboardRow` gate), an empty corpus, and on
 * `/games` an All-Time scope whose filters select zero rounds (`GameStatsViewBuilder#netJsonFor` —
 * a windowed scope keeps its chart at zero rounds, since the axis carries the columns and an
 * all-idle week is honest). Its absence is a normal case here, not an error, so this mounts nothing
 * and returns.
 *
 * The builders are date-agnostic by contract, so **every word is composed here**: the labels, the
 * tooltip details, and the tick candidates. Dates format through `i18n.js#getActiveLanguage()`
 * rather than the raw `<html lang>` — the same seam `compact-numbers.js` formats through, so a
 * date tick and the numbers beside it agree even under the footer selector's language override
 * (`result/result-overlays.js#progressionRows` states the same for its own rows).
 */
import { t, getActiveLanguage } from "./i18n.js";
import { formatGrouped } from "./compact-numbers.js";

// Both chart modules are reached through a memoized dynamic import, never a static one — the same
// seam and the same reason as `stats-panel-enhance.js#loadCharts`: this module's importer is
// imported statically by both fragment consumers, so it rides the eager boot graph, and a static
// edge here would drag both builders plus chart-math.js onto the game page's boot preload for a
// surface that renders only after a leaderboard-row click. `result/result-overlays.js` imports
// `buildProgressionLine` statically and is right to — that module is itself lazily loaded, so its
// static edge costs nothing. This one's would not be.
let columnsPromise = null;
function loadDivergingColumns() {
  columnsPromise ??= import("./charts/diverging-columns.js");
  return columnsPromise;
}

let linePromise = null;
function loadProgressionLine() {
  linePromise ??= import("./charts/progression-line.js");
  return linePromise;
}

// why: an axis this short or shorter is a week, where a reader locates a day by its weekday; a
// longer one is a month, where the weekday repeats four times over and the date is the handle. Not
// a window flag — the mount is never told which board it is drawing, and the axis length is the
// fact the naming actually turns on. The builder's own tick thinning stays continuous either way.
const WEEKDAY_TICK_MAX_DAYS = 7;

/** Mounts the net chart into `root`'s `.sp-net-chart` host, or does nothing when this fragment
 *  carries no net series (the cases the header enumerates). Skip-when-populated like the triad's
 *  own mounts, so a re-enhance is a no-op.
 *
 *  Awaited by `stats-panel-enhance.js#enhance`, whose promise `leaderboard-expansion.js
 *  #expandDetail` measures the fragment's settled height off — a chart mounted after that chain
 *  would be clipped by the accordion's max-height. */
export async function mountNetChart(root) {
  const host = root.querySelector(".sp-net-chart");
  const span = root.querySelector(".sp-net-data");
  if (host == null || span == null || host.firstChild != null) return;
  const series = JSON.parse(span.dataset.net);
  // Removed once read, the `.sp-band-data` sibling's move (leaderboard-expansion.js
  // #mountBandMatrix): an all-time career serializes every round into this one attribute, so the
  // node is multi-KB on a long career and nothing reads it twice — the board's fragment memory
  // (leaderboard-expansion.js `boards`) holds the fetched HTML, not this DOM, so a re-mount parses
  // a fresh span, and a same-key re-expand no longer re-mounts at all. It also tightens the
  // double-mount guard above: a second `enhance` interleaving before the await below resolves now
  // bails at the span check rather than relying on the post-await re-check alone.
  span.remove();
  const days = series.form === "days";
  const build = days
    ? (await loadDivergingColumns()).buildDivergingColumns
    : (await loadProgressionLine()).buildProgressionLine;
  // Re-check after the await: a second enhance() on the same root can interleave here (the guard
  // above ran before the module resolved), and a double mount would stack two charts in one host.
  if (host.firstChild != null) return;
  paintNetChart(host, build, days ? dayRows(series) : roundRows(series));
}

/** Draws the chart at the host's measured width and keeps it there. Both forms are measured — the
 *  caller owns measurement, per every builder's contract — so a width change rebuilds rather than
 *  reflows, exactly as `result-overlays.js#openProgressionOverlay` does for the same reason. The
 *  entrance plays on the first build only; a rebuild passes `motion: false`.
 *
 *  The observer is never torn down explicitly and never needs to be. A second `enhance` on the same
 *  root can't reach here (the skip-when-populated guard above), and a re-fetched fragment mounts a
 *  fresh host, leaving the old host and its observer unreachable together. */
function paintNetChart(host, build, rows) {
  let builtAt = host.clientWidth;
  host.appendChild(build(rows, { widthPx: builtAt, motion: true }));
  new ResizeObserver(() => {
    const width = host.clientWidth;
    // A width of 0 is a host that is not laid out — a retained leaderboard panel between boards
    // (leaderboard-expansion.js keeps the viewer row's mounted panel across rebuilds, detached
    // until its view is shown again): the observer fires on the detach, and a chart built for
    // nothing would only be thrown away again at re-attach. Keep the last real build; the next
    // real width decides whether it still fits (docs/PERFORMANCE.md § A rebuilt surface keeps its
    // mounted subtrees — the second corollary, with the counts).
    if (width === 0 || width === builtAt) return; // a height-only change is not a rebuild either
    builtAt = width;
    host.replaceChildren(build(rows, { widthPx: builtAt, motion: false }));
  }).observe(host);
}

/** One row per day of the window's axis, in axis order — the `"days"` form's fold. */
function dayRows(series) {
  // Resolved once for the whole axis rather than per day: both are properties of the series, and
  // `getActiveLanguage` reaches localStorage on every call.
  const format = {
    lang: getActiveLanguage(),
    tick:
      series.days.length <= WEEKDAY_TICK_MAX_DAYS
        ? { weekday: "short" }
        : { day: "numeric", month: "short" },
  };
  return series.days.map((_, i) => dayRow(series, i, format));
}

/** One day's row. The three states are mutually exclusive and read off the wire in priority order:
 *  a day after today is `future` whatever its (necessarily zero) counts say, then a day with no
 *  rounds is `idle`, and everything left is a day that was played. */
function dayRow(series, i, format) {
  const rounds = series.rounds[i];
  const state = series.future[i] ? "future" : rounds === 0 ? "idle" : "played";
  const date = dayDate(series.days[i]);
  return {
    value: series.nets[i],
    state,
    label: date.toLocaleDateString(format.lang, {
      weekday: "short",
      day: "numeric",
      month: "short",
    }),
    detail: dayDetail(state, series.nets[i], rounds),
    tick: date.toLocaleDateString(format.lang, format.tick),
  };
}

/** A `YYYY-MM-DD` board day as a local `Date`. Built from split components on purpose: passing the
 *  string to `new Date()` parses it as UTC, which renders as the PREVIOUS day in every western
 *  time zone — and the key is already a calendar day in the board's own zone, so there is nothing
 *  left to convert. */
function dayDate(day) {
  const [year, month, date] = day.split("-").map(Number);
  return new Date(year, month - 1, date);
}

/** The day's tooltip detail: its exact net and the rounds behind it, or the fact that it had none.
 *  A future day gets no detail at all — nothing has happened there to state, and the label already
 *  names the day. The net is exact and locale-grouped (the column's own tip is the compacted mark;
 *  the tooltip keeps the truth, the vocabulary's own split). */
function dayDetail(state, net, rounds) {
  if (state === "future") return null;
  if (state === "idle") return t("leaderboardNetDayRounds", 0);
  const roundText = t(
    rounds === 1 ? "leaderboardNetDayRound" : "leaderboardNetDayRounds",
    rounds,
  );
  return `${signed(net)} · ${roundText}`;
}

/** One row per round of the scope, in the wire's own order — chronological, play order as the
 *  tie-break (`StatsPanelNetSeries`' class Javadoc owns that rule; this fold never re-sorts) —
 *  the `"rounds"` form's fold, and deliberately
 *  the same row *shape* `result-overlays.js#progressionRows` builds for the session progression: the
 *  cumulative standing as `value`, the round and its day as `label`, `delta → cumulative` as
 *  `detail`, a short date as the tick candidate. The two `detail` strings do not share a number
 *  formatter, though: `signed` here wraps `formatGrouped` (locale grouping, U+2212 minus — the rule
 *  `compact-numbers.js` states), while `result-history.js#formatSignedNumber` returns a bare
 *  `String(value)` with an ASCII hyphen and no grouping. Reconciling that is the result screen's
 *  side (`todo/abstraction-hunt-result-screen.md`). The wire carries per-round deltas rather than
 *  the running standing, so the fold happens here (`StatsPanelNetSeries`'s own doc-comment has
 *  why). */
function roundRows(series) {
  const lang = getActiveLanguage();
  let cumulative = 0;
  return series.deltas.map((delta, i) => {
    cumulative += delta;
    const date = new Date(series.at[i]).toLocaleDateString(lang, {
      day: "numeric",
      month: "short",
    });
    return {
      label: `${t("resultRound", i + 1)} · ${date}`,
      detail: `${signed(delta)} → ${signed(cumulative)}`,
      value: cumulative,
      tick: date,
    };
  });
}

/** The exact, locale-grouped value with an explicit sign. `formatGrouped` already carries the
 *  typographic minus; only a non-negative value needs the plus put back on. */
function signed(value) {
  return value > 0 ? `+${formatGrouped(value)}` : formatGrouped(value);
}
